/**
 * Semantic status colors for recording states.
 *
 * These colors are INTENTIONALLY not part of brand customization.
 * They convey semantic meaning:
 * - Red = recording/active
 * - Green = ready/completed
 * - Blue = paused/starting
 * - Amber = warning (e.g. no audio detected while recording)
 *
 * Do NOT change these based on brand configuration.
 *
 * Each status has:
 * - bg: Light background color for pills/badges
 * - text: Text color that contrasts with the light background
 * - solid: Solid color for icons, indicators, or filled elements
 */
export const STATUS_COLORS = {
  ready: {
    bg: '#E4F5ED',
    text: '#347A59',
    solid: '#10b981', // emerald-500
  },
  recording: {
    bg: '#FFEFF3',
    text: '#BF3131',
    solid: '#ef4444', // red-500
  },
  transcribing: {
    bg: '#FFEFF3',
    text: '#BF3131',
    solid: '#ef4444', // red-500
  },
  paused: {
    bg: '#FFFBD9',
    text: '#6B6215',
    solid: '#3b82f6', // blue-500
  },
  starting: {
    bg: '#E4F5ED',
    text: '#347A59',
    solid: '#10b981', // emerald-500
  },
  completed: {
    bg: '#E4F5ED',
    text: '#347A59',
    solid: '#10b981', // emerald-500
  },
  warning: {
    bg: '#FFF4E5',
    text: '#B45309',
    solid: '#ED6C02', // MUI warning.main
  },
} as const;

/**
 * Recording indicator colors (always red regardless of brand).
 * Used for the recording dot animation and similar always-visible indicators.
 */
export const RECORDING_COLORS = {
  recording: '#BF3131',
  recordingDark: '#991B1B',
  recordingLight: '#FFEFF3',
} as const;

/**
 * Connection state colors for WebSocket/network indicators.
 */
export const CONNECTION_COLORS = {
  connected: '#347A59',
  connecting: '#D97706',
  disconnected: '#9CA3AF',
  error: '#BF3131',
  processing: '#7C3AED',
} as const;

/**
 * Editor-specific colors for elements rendered outside MUI theme context.
 * Used in DOM manipulation where theme is not accessible.
 */
export const EDITOR_COLORS = {
  placeholder: '#666666',
} as const;

export type StatusType = keyof typeof STATUS_COLORS;

/**
 * Status configurations for recording control bar.
 * Combines colors with UI metadata (text, icons).
 * Icons are defined as React elements in the component that uses this.
 */
export type StatusConfigBase = {
  bg: string;
  textColor: string;
  text: string;
  showIcon: boolean;
};

export const STATUS_CONFIGS: Record<string, StatusConfigBase> = {
  READY: {
    bg: STATUS_COLORS.ready.bg,
    textColor: STATUS_COLORS.ready.text,
    text: 'READY',
    showIcon: false,
  },
  STARTING: {
    bg: STATUS_COLORS.starting.bg,
    textColor: STATUS_COLORS.starting.text,
    text: 'STARTING',
    showIcon: false,
  },
  TRANSCRIBING: {
    bg: STATUS_COLORS.transcribing.bg,
    textColor: STATUS_COLORS.transcribing.text,
    text: 'TRANSCRIBING',
    showIcon: true,
  },
  PAUSED: {
    bg: STATUS_COLORS.paused.bg,
    textColor: STATUS_COLORS.paused.text,
    text: 'PAUSED',
    showIcon: true,
  },
  COMPLETE: {
    bg: STATUS_COLORS.completed.bg,
    textColor: STATUS_COLORS.completed.text,
    text: 'COMPLETE',
    showIcon: true,
  },
  NO_AUDIO: {
    bg: STATUS_COLORS.warning.bg,
    textColor: STATUS_COLORS.warning.text,
    text: 'NO AUDIO',
    showIcon: true,
  },
} as const;
